import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Zap, TriangleAlert, Plus, ArrowRight } from "lucide-react";
import { fetchScans, fetchFindings, type Scan, type Finding } from "../lib/api";
import {
  PageHeader,
  StatCard,
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
  Button,
} from "../components/ui";

export default function Dashboard() {
  const navigate = useNavigate();
  const [scans, setScans] = useState<Scan[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([fetchScans(), fetchFindings()])
      .then(([s, f]) => {
        setScans(Array.isArray(s) ? s : []);
        setFindings(Array.isArray(f) ? f : []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const active = scans.filter((s) => s.status === "running").length;
  const critical = findings.filter((f) => f.severity === "critical").length;

  return (
    <div className="space-y-7">
      <PageHeader
        title="Dashboard"
        description="A live overview of your attack surface and scan activity."
      >
        <Button onClick={() => navigate("/scans", { state: { openNew: true } })}>
          <Plus className="h-4 w-4" /> New scan
        </Button>
      </PageHeader>

      {loading && <LoadingState label="Loading your workspace…" />}

      {error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total scans"
              value={scans.length}
              hint="Across all projects"
              accent="volt"
              icon={<Radar className="h-4 w-4" />}
            />
            <StatCard
              label="Active now"
              value={active}
              hint={active > 0 ? "Scans in progress" : "Nothing running"}
              accent="sky"
              icon={<Zap className="h-4 w-4" />}
            />
            <StatCard
              label="Findings"
              value={findings.length}
              hint="Vulnerabilities discovered"
              accent="orange"
              icon={<TriangleAlert className="h-4 w-4" />}
            />
            <StatCard
              label="Critical"
              value={critical}
              hint={critical > 0 ? "Needs attention" : "No critical issues"}
              accent="red"
              icon={<TriangleAlert className="h-4 w-4" />}
            />
          </div>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-base font-semibold tracking-tight text-mist-100">
                Recent scans
              </h2>
              <button
                onClick={() => navigate("/scans")}
                className="inline-flex items-center gap-1 text-sm text-mist-400 transition-colors hover:text-mist-100"
              >
                View all <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {scans.length === 0 ? (
              <EmptyState
                icon={<Radar className="h-6 w-6" />}
                title="No scans yet"
                description="Launch your first scan to start discovering vulnerabilities across your targets."
                action={
                  <Button size="sm" onClick={() => navigate("/scans", { state: { openNew: true } })}>
                    <Plus className="h-4 w-4" /> Launch a scan
                  </Button>
                }
              />
            ) : (
              <div className="table-shell">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-mist-500">
                      <th className="px-4 py-3 font-medium">Target</th>
                      <th className="px-4 py-3 font-medium">Mode</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Findings</th>
                      <th className="px-4 py-3 font-medium">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.slice(0, 6).map((s) => (
                      <tr
                        key={s.id}
                        onClick={() => navigate(`/scans/${s.id}`)}
                        className="table-row cursor-pointer border-b border-line/60 last:border-0"
                      >
                        <td className="max-w-[260px] truncate px-4 py-3 font-mono text-xs text-mist-200">
                          {s.target}
                        </td>
                        <td className="px-4 py-3 capitalize text-mist-400">{s.mode}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={s.status} />
                        </td>
                        <td className="px-4 py-3 tabular-nums text-mist-300">{s.findings_count}</td>
                        <td className="px-4 py-3 text-mist-500">
                          {new Date(s.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
